import { Box, Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Component from "components/Component";
import DefaultLoader from "components/common/DefaultLoader";
import Disabled from "components/common/Disabled";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { connect } from "react-redux";
import { uploadImage } from "store/actions/input/images/imagesActions";
import { cacheImage } from "store/actions/temp/images/imagesActions";
import { v4 as uuidv4 } from "uuid";

const SUPPORTED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/bmp",
  "image/gif",
];

const InsuredLogo = ({
  insuredLogo,
  updateProgram,
  uploadImage,
  cacheImage,
  imageCache,
  remoteImages,
}) => {
  const theme = useTheme();
  const [uploadingLocalId, setUploadingLocalId] = useState(null);
  const remoteImage = insuredLogo?.localId
    ? remoteImages?.[insuredLogo.localId]
    : null;
  const cachedUrl = remoteImage
    ? imageCache?.[remoteImage.submissionId]?.[remoteImage.imageId]
    : null;

  useEffect(() => {
    if (
      remoteImage?.submissionId &&
      remoteImage?.imageId &&
      cachedUrl == null
    ) {
      cacheImage(
        remoteImage.submissionId,
        remoteImage.imageId,
        remoteImage.imageType
      );
    }
  }, [
    cacheImage,
    cachedUrl,
    remoteImage?.imageId,
    remoteImage?.imageType,
    remoteImage?.submissionId,
  ]);

  useEffect(() => {
    if (uploadingLocalId && remoteImages?.[uploadingLocalId]) {
      setUploadingLocalId(null);
    }
  }, [remoteImages, uploadingLocalId]);

  const logoUrl = remoteImage ? cachedUrl ?? "/loading.gif" : null;
  const isUploading = uploadingLocalId != null;

  const handleRemove = () => {
    updateProgram({ insuredLogo: null });
    setUploadingLocalId(null);
  };

  const handleFile = (file) => {
    if (!file) {
      return;
    }
    const mimeType = file.type?.toLowerCase();
    if (!SUPPORTED_IMAGE_TYPES.includes(mimeType)) {
      return;
    }
    const localId = uuidv4();
    uploadImage(file, localId);
    updateProgram({
      insuredLogo: {
        localId,
        name: file.name,
      },
    });
    setUploadingLocalId(localId);
  };

  const accept = SUPPORTED_IMAGE_TYPES.join(",");

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop: (files) => handleFile(files?.[0]),
    accept,
    multiple: false,
    noClick: true,
    disabled: isUploading,
  });

  return (
    <Component title={"Insured Logo"}>
      <Stack direction={"column"} spacing={2} alignItems={"center"}>
        <Disabled ifReadOnly>
          <Box
            {...getRootProps()}
            sx={{
              width: "100%",
              border: "1px dashed #c7cbd1",
              borderRadius: 2,
              padding: 2,
              backgroundColor: isDragActive ? "#f9fafb" : "transparent",
              textAlign: "center",
              cursor: isUploading ? "not-allowed" : "pointer",
            }}
          >
            <input {...getInputProps()} />
            {logoUrl ? (
              <Box
                component={"img"}
                src={logoUrl}
                alt={insuredLogo?.name ?? "Insured logo"}
                sx={{
                  maxHeight: 90,
                  maxWidth: "100%",
                  objectFit: "contain",
                  borderRadius: 2,
                  display: "block",
                  margin: "0 auto 8px",
                }}
              />
            ) : (
              <></>
            )}
            <Typography variant={"body2"} color={"text.secondary"}>
              {isDragActive
                ? "Drop the image here"
                : "Drag and drop a logo here, or use the button below"}
            </Typography>
          </Box>
        </Disabled>
        <Stack direction={"row"} spacing={1}>
          <Disabled ifReadOnly>
            <Button
              variant={"contained"}
              disableElevation
              onClick={open}
              disabled={isUploading}
            >
              {isUploading ? (
                <DefaultLoader color={theme.brand.white} size={18} />
              ) : logoUrl ? (
                "Replace Logo"
              ) : (
                "Upload Logo"
              )}
            </Button>
          </Disabled>
          <Disabled ifReadOnly>
            <Button
              variant={"contained"}
              color={"secondary"}
              onClick={handleRemove}
              disabled={!logoUrl || isUploading}
            >
              {"Remove"}
            </Button>
          </Disabled>
        </Stack>
      </Stack>
    </Component>
  );
};

const mapStateToProps = (state) => ({
  imageCache: state.temp.images?.cache,
  remoteImages: state.input.images?.remote,
});

const mapDispatchToProps = {
  uploadImage,
  cacheImage,
};

export default connect(mapStateToProps, mapDispatchToProps)(InsuredLogo);
